import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const SettingsContext = createContext();

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState({
    orderCutoffTime: '16:00', // 4:00 PM default
    isOrderingOpen: true,
    deliverySettings: {
      enableDeliveryFee: false,
      deliveryFee: 50.00,
      freeDeliveryThreshold: 500.00,
      freeDeliveryEnabled: true
    },
    paymentSettings: {
      qrCodeImage: '/qr-code-payment.png',
      qrCodeInstructions: 'Scan this QR code with your preferred payment app (Paytm, Google Pay, PhonePe, etc.) and enter the order amount.',
      paymentNote: 'Food Order'
    }
  });
  const [loading, setLoading] = useState(true);
  const { user, token } = useAuth();

  // Use environment variable for API URL
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  // Fetch settings from backend
  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/settings`);
      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({
          ...prev,
          ...data,
          // Map backend settings to frontend format
          orderCutoffTime: data.cutOffTime || prev.orderCutoffTime,
          isOrderingOpen: data.orderingEnabled !== false,
          whatsappGroupLink: data.whatsappGroupLink || prev.whatsappGroupLink,
          contactInfo: data.contactInfo || prev.contactInfo,
          paymentSettings: data.paymentSettings || prev.paymentSettings
        }));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  // Update settings in backend
  const updateSettings = async (newSettings) => {
    if (!user || !token || !user.isAdmin) {
      console.error('Only admins can update settings');
      return false;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          cutOffTime: newSettings.orderCutoffTime,
          orderingEnabled: newSettings.isOrderingOpen,
          whatsappGroupLink: newSettings.whatsappGroupLink,
          contactInfo: {
            whatsappSupportNumber: newSettings.whatsappSupportNumber
          }
          // Add other settings as needed
        })
      });

      if (response.ok) {
        const updatedSettings = await response.json();
        setSettings(prev => ({
          ...prev,
          ...newSettings,
          contactInfo: updatedSettings.contactInfo || prev.contactInfo,
          paymentSettings: updatedSettings.paymentSettings || prev.paymentSettings
        }));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error updating settings:', error);
      return false;
    }
  };

  // Update QR code settings
  const updateQRCodeSettings = async (qrSettings) => {
    if (!user || !token || !user.isAdmin) {
      console.error('Only admins can update QR code settings');
      return false;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/settings/qr-code`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(qrSettings)
      });

      if (response.ok) {
        const updatedSettings = await response.json();
        setSettings(prev => ({
          ...prev,
          paymentSettings: updatedSettings.paymentSettings
        }));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error updating QR code settings:', error);
      return false;
    }
  };

  // Update delivery settings
  const updateDeliverySettings = async (deliverySettings) => {
    if (!user || !token || !user.isAdmin) {
      console.error('Only admins can update delivery settings');
      return false;
    }

    try {
      console.log('Sending delivery settings:', deliverySettings);
      console.log('Token:', token);
      
      const response = await fetch(`${API_BASE_URL}/settings/delivery`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(deliverySettings)
      });

      console.log('Response status:', response.status);
      
      if (response.ok) {
        const updatedSettings = await response.json();
        console.log('Updated settings:', updatedSettings);
        setSettings(prev => ({
          ...prev,
          deliverySettings: updatedSettings.deliverySettings
        }));
        return true;
      } else {
        const errorData = await response.json();
        console.error('Response error:', errorData);
        return false;
      }
    } catch (error) {
      console.error('Error updating delivery settings:', error);
      return false;
    }
  };

  // Load settings from localStorage on mount (fallback)
  useEffect(() => {
    const savedSettings = localStorage.getItem('appSettings');
    if (savedSettings) {
      const parsed = JSON.parse(savedSettings);
      setSettings(prev => ({ ...prev, ...parsed }));
    }
  }, []);

  // Fetch settings from backend on mount
  useEffect(() => {
    fetchSettings();
  }, []);

  // Save settings to localStorage whenever they change (fallback)
  useEffect(() => {
    localStorage.setItem('appSettings', JSON.stringify(settings));
  }, [settings]);

  // Calculate next order deadline
  const getNextOrderDeadline = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    // Parse cut-off time
    const [hours, minutes] = settings.orderCutoffTime.split(':').map(Number);
    const cutoffTime = new Date(today);
    cutoffTime.setHours(hours, minutes, 0, 0);
    
    // If today's cutoff has passed, move to next day
    if (now > cutoffTime) {
      cutoffTime.setDate(cutoffTime.getDate() + 1);
    }
    
    return cutoffTime;
  };

  // Calculate next delivery date
  const getNextDeliveryDate = () => {
    const now = new Date();
    const currentHour = now.getHours();
    
    // If current time is after 16:00 (4 PM), delivery is next day
    // Otherwise, delivery is same day
    const deliveryDate = new Date();
    if (currentHour >= 16) {
      deliveryDate.setDate(deliveryDate.getDate() + 1);
    }
    
    return deliveryDate;
  };

  // Check if ordering is currently open
  const isOrderingCurrentlyOpen = () => {
    if (!settings.isOrderingOpen) return false;
    
    const now = new Date();
    const deadline = getNextOrderDeadline();
    return now < deadline;
  };

  // Get time remaining until deadline
  const getTimeUntilDeadline = () => {
    const now = new Date();
    const deadline = getNextOrderDeadline();
    const timeDiff = deadline.getTime() - now.getTime();
    
    if (timeDiff <= 0) {
      return { hours: 0, minutes: 0, seconds: 0, total: 0 };
    }
    
    const hours = Math.floor(timeDiff / (1000 * 60 * 60));
    const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);
    
    return { hours, minutes, seconds, total: timeDiff };
  };

  const value = {
    settings,
    loading,
    updateSettings,
    updateQRCodeSettings,
    updateDeliverySettings,
    getNextOrderDeadline,
    getNextDeliveryDate,
    isOrderingCurrentlyOpen,
    getTimeUntilDeadline
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}; 